<template>
  <div>
    <!-- Overbought -->
    <helpers-section-sub-header class="mt-8 mb-2">
      Kaufman Adaptive Moving Average (KAMA)
    </helpers-section-sub-header>
    <p>
      KAMA adjusts as price swings widen and can be used to identify changes in trend. Short KAMA
      uses 10 trading days for the efficiency ratio, 2 days for the fastest EMA and 30 days for the
      slowest EMA. LongKAMA uses 10 trading days for the efficiency ratio, 5 days for the fastest
      EMA and 30 days for the slowest EMA
    </p>

    <v-data-table :headers="ta_headers" :items="criteria.kama_rules" class="mt-6 mb-8">
      <template #top>
        <v-dialog v-model="taDialog" scrollable max-width="450px">
          <template #activator="{ props }">
            <v-btn color="primary" dark class="mb-4" v-bind="props"> New KAMA Rule </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ taTitle }}</span>
            </v-card-title>

            <v-card-text style="height: 350px">
              <p>
                KAMA adjusts as price swings widen and can be used to identify changes in trend.
                Short KAMA uses 10 trading days for the efficiency ratio, 2 days for the fastest EMA
                and 30 days for the slowest EMA. Long KAMA uses 10 trading days for the efficiency
                ratio, 5 days for the fastest EMA and 30 days for the slowest EMA
              </p>
              <v-container>
                <v-row density="compact">
                  <v-col cols="12" sm="12">
                    <v-checkbox v-model="editedTa.enabled" density="compact" label="Rule Enabled" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedTa.long_short"
                      :items="long_short"
                      label="Signal Type"
                      hint="Long or Short Signal"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="editedTa.term"
                      :items="['long', 'short']"
                      label="Long or Short Term KAMA"
                      hint="Long or Short-Term KAMA"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="12">
                    <v-select
                      v-model="editedTa.above_below"
                      :items="['above', 'below']"
                      label="Current Price Above or Below KAMA"
                      hint="Current Price Above or Below KAMA"
                      density="compact"
                      hide-details
                      variant="outlined"
                    />
                  </v-col>
                  <v-col cols="12" sm="12">
                    <v-text-field
                      v-model="editedTa.consecutive_days"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="Consecutive days price has been above or below KAMA"
                    />
                  </v-col>

                  <v-col cols="12" sm="12">
                    <v-text-field
                      v-model="editedTa.percent"
                      density="compact"
                      hide-details
                      variant="outlined"
                      label="% Closing price above or below KAMA"
                      hint="Closing Price % Above or Below KAMA"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <div class="flex-grow-1" />
              <v-btn color="blue-darken-1" text @click="closeTa"> Cancel </v-btn>
              <v-btn color="blue-darken-1" text @click="saveTa"> Save </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <template #item.action="{ item }">
        <v-icon small class="mr-2" @click="editTa(item)"> edit </v-icon>
        <v-icon small @click="deleteTa(item)"> delete </v-icon>
      </template>

      <template #item.enabled="{ item }">
        <v-switch v-model="item.enabled" color="primary" />
      </template>
      <template #no-data> No KAMA signals configured </template>
    </v-data-table>
  </div>
</template>

<script>
// Modules
import { mapFields } from '@/store/helpers'

export default {
  components: {},
  data() {
    return {
      taDialog: false,
      long_short: ['long', 'short'],
      ta_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Rule Type',
          align: 'left',
          sortable: true,
          key: 'long_short'
        },
        {
          title: 'Short/Long Term KAMA',
          align: 'left',
          sortable: true,
          key: 'term'
        },
        {
          title: 'Above/Below',
          align: 'left',
          sortable: true,
          key: 'above_below'
        },
        {
          title: 'Consecutive Days',
          align: 'left',
          sortable: true,
          key: 'consecutive_days'
        },
        {
          title: '% Change',
          align: 'left',
          sortable: true,
          key: 'percent'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      editedTa: {
        enabled: true,
        above_below: 'above',
        term: 'short',
        consecutive_days: 5,
        percent: 3,
        long_short: 'long'
      },
      defaultTa: {
        enabled: true,
        above_below: 'above',
        term: 'short',
        consecutive_days: 5,
        percent: 3,
        long_short: 'long'
      },
      editedIndex: -1
    }
  },

  computed: {
    taTitle() {
      return this.editedIndex === -1 ? 'New Rule' : 'Edit Rule'
    },
    ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.trend_signal',
      enabled: 'app.whaleTraderCriteria.signals.trend_signal.enabled',
      signalView: 'app.signalView',
      exit_time_periods: 'app.exit_time_periods'
    })
  },

  watch: {
    taDialog(val) {
      val || this.closeTa()
    }
  },

  created() {},

  methods: {
    editTa(item) {
      this.editedIndex = this.criteria.kama_rules.indexOf(item)
      this.editedTa = Object.assign({}, item)
      this.taDialog = true
    },

    deleteTa(item) {
      const index = this.criteria.kama_rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.criteria.kama_rules.splice(index, 1)
    },

    closeTa() {
      this.taDialog = false
      setTimeout(() => {
        this.editedTa = Object.assign({}, this.defaultTa)
        this.editedIndex = -1
      }, 300)
    },

    saveTa() {
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.kama_rules[this.editedIndex], this.editedTa)
      } else {
        if (!this.criteria.kama_rules) {
          this.criteria.kama_rules = []
        }
        this.criteria.kama_rules.push(this.editedTa)
      }
      this.closeTa()
    }
  }
}
</script>
